import { useLocation, Routes, Route } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import "../../index.css";
import { ProtectedRoute } from "../UtilRoutes/PrivateRoute";
import AnonRoute from "../UtilRoutes/AnonRoute";
import Loading from "../Loading";

// 👇 Lazy imports
const Login = lazy(() => import("../../pages/Login"));
const Home = lazy(() => import("../../pages"));
const Services = lazy(() => import("../../pages/Services"));
const Network = lazy(() => import("../../pages/Network"));
const Maintenance = lazy(() => import("../../pages/Maintenance"));
const Parcel = lazy(() => import("../../pages/Parcel"));
const Canteen = lazy(() => import("../../pages/Canteen"));
const Gym = lazy(() => import("../../pages/Gym"));
const Restaurant = lazy(() => import("../../pages/Restaurant"));
const Signup = lazy(() => import("../../pages/Signup"));
const NewTicket = lazy(() => import("../../pages/Maintenance/newTicket"));
const BusinessTickets = lazy(() => import("../../pages/Maintenance/BusinessTickets"));
const NewGymEvent = lazy(() => import("../../pages/Gym/newEvent"));
const NewCanteenOffer = lazy(() => import("../../pages/Canteen/newOffer"));
const Account = lazy(() => import("../../pages/Profile"));
const MyProfile = lazy(() => import("../../pages/Profile/MyProfile"));
const MyCharsharing = lazy(() => import("../../pages/Profile/MyCarsharing"));
const Business = lazy(() => import("../../pages/Network/Business"));
const Partners = lazy(() => import("../../pages/Network/Partners"));
const Charsharing = lazy(() => import("../../pages/Network/Carsharing"));
const Activitysharing = lazy(() => import("../../pages/Network/Activitysharing"));
const Bmc = lazy(() => import("../../pages/BMC"));
const QrScanner = lazy(() => import("../../pages/QrScanner"));
const Salas = lazy(() => import("../../pages/SalaBooking"));
const SalaPicker = lazy(() => import("../../pages/SalaBooking/SalaPicker"));
const EditProfile = lazy(() => import("../../pages/Profile/MyProfile/EditProfile"));
const ChangePass = lazy(() => import("../../pages/Profile/MyProfile/ChangePass"));
const CreateBusiness = lazy(() => import("../../pages/Business/CreateBusiness"));
const AdminPanel = lazy(() => import("../../pages/Admin"));
const CanteenAdmin = lazy(() => import("../../pages/Canteen/Admin"));
const AdminGym = lazy(() => import("../../pages/Gym/Admin"));
const UsersPanel = lazy(() => import("../../pages/Users"));
const BusinessPanel = lazy(() => import("../../pages/BusinessPanel"));
const AdminMaintenance = lazy(() => import("../../pages/Maintenance/Admin"));
const AdminParcel = lazy(() => import("../../pages/Parcel/Admin"));
const AdminSalas = lazy(() => import("../../pages/SalaBooking/Admin"));
const EditUser = lazy(() => import("../../pages/Users/EditUser"));
const EditBusiness = lazy(() => import("../../pages/Business/editBusiness"));
const Contact = lazy(() => import("../../pages/BMC/Contact"));
const AboutBmc = lazy(() => import("../../pages/BMC/AboutBmc"));
const BmcEvents = lazy(() => import("../../pages/BMC/BmcEvents"));
const BusinessCard = lazy(() => import("../../pages/Network/Business/BusinessCard"));
const EditMyBusiness = lazy(() => import("../../pages/Profile/MyProfile/editMyBusiness"));
const EventsBMC = lazy(() => import("../../pages/Admin/EventsBMC"));
const NewEventBmc = lazy(() => import("../../pages/Admin/EventsBMC/New"));
const Events = lazy(() => import("../../pages/Network/Events"));
const MyEvents = lazy(() => import("../../pages/Profile/MyEvents"));
const NewEvent = lazy(() => import("../../pages/Network/Events/New"));
const Statistics = lazy(() => import("../../pages/Statistics"));
const SliderAdmin = lazy(() => import("../../pages/Admin/Slider"));
const Notis = lazy(() => import("../../pages/Profile/Notis"));
const NotisDetails = lazy(() => import("../../pages/Profile/Notis/NotisDetails"));
const NewActivitySharing = lazy(() => import("../../pages/Network/Activitysharing/New"));
const Invitation = lazy(() => import("../../pages/Invitation"));
const TemporalAccess = lazy(() => import("../../pages/TemporalAccess"));
const AdminPicker = lazy(() => import("../../pages/SalaBooking/AdminPicker"));
const Legal = lazy(() => import("../../pages/Legal"));
const InfoLegal = lazy(() => import("../../pages/InfoLegal"));
const LegalLSSI = lazy(() => import("../../pages/Legal/Info"));
const PrivacityPolicy = lazy(() => import("../../pages/Privacity"));
const CookiesPolicy = lazy(() => import("../../pages/Cookies"));
const Categories = lazy(() => import("../../pages/Admin/Categories"));
const NewEventWebBmc = lazy(() => import("../../pages/Admin/EventsWebBMC/New"));
const EventsWebBMC = lazy(() => import("../../pages/Admin/EventsWebBMC"));
const EditEventBmc = lazy(() => import("../../pages/Admin/EventsBMC/Edit"));
const EditEventBusiness = lazy(() => import("../../pages/Network/Events/Edit"));
const Offers = lazy(() => import("../../pages/Network/Offers"));
const AdminParcelClients = lazy(() => import("../../pages/ParcelClients/Admin"));
const EventDetail = lazy(() => import("../EventDetail"));
const MaintenancePage = lazy(() => import("../../pages/Maintenance/MaintenancePage"));
const ImportUsers = lazy(() => import("../../pages/Admin/ImportUsers"));

const Router = () => {
  // const navigation = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/maintenance-mode" element={<MaintenancePage />} />
        <Route path="/invitation/:id" element={<TemporalAccess />} />
        <Route
          path="/login"
          element={
            <AnonRoute>
              <Login />
            </AnonRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute redirectPath="/login">
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/qrscanner"
          element={
            <ProtectedRoute redirectPath="/login">
              <QrScanner />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute redirectPath="/login">
              <Account />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-profile"
          element={
            <ProtectedRoute redirectPath="/login">
              <MyProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories"
          element={
            <ProtectedRoute redirectPath="/login">
              <Categories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-my-business"
          element={
            <ProtectedRoute redirectPath="/login">
              <EditMyBusiness />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-profile"
          element={
            <ProtectedRoute redirectPath="/login">
              <EditProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-user"
          element={
            <ProtectedRoute redirectPath="/login">
              <EditUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-business"
          element={
            <ProtectedRoute redirectPath="/login">
              <EditBusiness />
            </ProtectedRoute>
          }
        />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute redirectPath="/login">
              <ChangePass />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notis"
          element={
            <ProtectedRoute redirectPath="/login">
              <Notis />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notis-details"
          element={
            <ProtectedRoute redirectPath="/login">
              <NotisDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-carsharing"
          element={
            <ProtectedRoute redirectPath="/login">
              <MyCharsharing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/services"
          element={
            <ProtectedRoute redirectPath="/login">
              <Services />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gym"
          element={
            <ProtectedRoute redirectPath="/login">
              <Gym />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gym-admin"
          element={
            <ProtectedRoute redirectPath="/login">
              <AdminGym />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salas"
          element={
            <ProtectedRoute redirectPath="/login">
              <Salas />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salas-admin"
          element={
            <ProtectedRoute redirectPath="/login">
              <AdminSalas />
            </ProtectedRoute>
          }
        />
        <Route
          path="/import-users"
          element={
            <ProtectedRoute redirectPath="/login">
              <ImportUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-book"
          element={
            <ProtectedRoute redirectPath="/login">
              <SalaPicker />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parcel"
          element={
            <ProtectedRoute redirectPath="/login">
              <Parcel />
            </ProtectedRoute>
          }
        />

        <Route
          path="/parcel-admin"
          element={
            <ProtectedRoute redirectPath="/login">
              <AdminParcel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parcel-admin-clients"
          element={
            <ProtectedRoute redirectPath="/login">
              <AdminParcelClients />
            </ProtectedRoute>
          }
        />

        <Route
          path="/users-panel"
          element={
            <ProtectedRoute redirectPath="/login">
              <UsersPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/business-panel"
          element={
            <ProtectedRoute redirectPath="/login">
              <BusinessPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/canteen"
          element={
            <ProtectedRoute redirectPath="/login">
              <Canteen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/canteen-admin"
          element={
            <ProtectedRoute redirectPath="/login">
              <CanteenAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/slider-admin"
          element={
            <ProtectedRoute redirectPath="/login">
              <SliderAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-invitation"
          element={
            <ProtectedRoute redirectPath="/login">
              <Invitation />
            </ProtectedRoute>
          }
        />

        <Route
          path="/maintenance"
          element={
            <ProtectedRoute redirectPath="/login">
              <Maintenance />
            </ProtectedRoute>
          }
        />

        <Route
          path="/maintenance-admin"
          element={
            <ProtectedRoute redirectPath="/login">
              <AdminMaintenance />
            </ProtectedRoute>
          }
        />

        <Route
          path="/maintenance-details"
          element={
            <ProtectedRoute redirectPath="/login">
              <BusinessTickets />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-gym-event"
          element={
            <ProtectedRoute redirectPath="/login">
              <NewGymEvent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-canteen-offer"
          element={
            <ProtectedRoute redirectPath="/login">
              <NewCanteenOffer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-ticket"
          element={
            <ProtectedRoute redirectPath="/login">
              <NewTicket />
            </ProtectedRoute>
          }
        />
        <Route
          path="/restaurant"
          element={
            <ProtectedRoute redirectPath="/login">
              <Restaurant />
            </ProtectedRoute>
          }
        />
        <Route
          path="/network"
          element={
            <ProtectedRoute redirectPath="/login">
              <Network />
            </ProtectedRoute>
          }
        />
        <Route
          path="/business"
          element={
            <ProtectedRoute redirectPath="/login">
              <Business />
            </ProtectedRoute>
          }
        />
        <Route
          path="/business-card"
          element={
            <ProtectedRoute redirectPath="/login">
              <BusinessCard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/partners"
          element={
            <ProtectedRoute redirectPath="/login">
              <Partners />
            </ProtectedRoute>
          }
        />
        <Route
          path="/carsharing"
          element={
            <ProtectedRoute redirectPath="/login">
              <Charsharing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activitysharing"
          element={
            <ProtectedRoute redirectPath="/login">
              <Activitysharing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activitysharing/new"
          element={
            <ProtectedRoute redirectPath="/login">
              <NewActivitySharing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bmc"
          element={
            <ProtectedRoute redirectPath="/login">
              <Bmc />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bmc-events"
          element={
            <ProtectedRoute redirectPath="/login">
              <BmcEvents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-event-bmc"
          element={
            <ProtectedRoute redirectPath="/login">
              <EditEventBmc />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-event-business"
          element={
            <ProtectedRoute redirectPath="/login">
              <EditEventBusiness />
            </ProtectedRoute>
          }
        />

        <Route
          path="/about-bmc"
          element={
            <ProtectedRoute redirectPath="/login">
              <AboutBmc />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contact"
          element={
            <ProtectedRoute redirectPath="/login">
              <Contact />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute redirectPath="/login">
              <AdminPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/statistics"
          element={
            <ProtectedRoute redirectPath="/login">
              <Statistics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <ProtectedRoute redirectPath="/login">
              <Signup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-business"
          element={
            <ProtectedRoute redirectPath="/login">
              <CreateBusiness />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events-bmc-admin"
          element={
            <ProtectedRoute redirectPath="/login">
              <EventsBMC />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-event-bmc"
          element={
            <ProtectedRoute redirectPath="/login">
              <NewEventBmc />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events-bmc-web-admin"
          element={
            <ProtectedRoute redirectPath="/login">
              <EventsWebBMC />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-event-bmc-web"
          element={
            <ProtectedRoute redirectPath="/login">
              <NewEventWebBmc />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events-business"
          element={
            <ProtectedRoute redirectPath="/login">
              <Events />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events-business/:id"
          element={
            <ProtectedRoute redirectPath="/login">
              <EventDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/offers-business"
          element={
            <ProtectedRoute redirectPath="/login">
              <Offers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-events"
          element={
            <ProtectedRoute redirectPath="/login">
              <MyEvents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-event"
          element={
            <ProtectedRoute redirectPath="/login">
              <NewEvent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/books-admin"
          element={
            <ProtectedRoute redirectPath="/login">
              <AdminPicker />
            </ProtectedRoute>
          }
        />
        <Route
          path="/legal"
          element={
            <ProtectedRoute redirectPath="/login">
              <Legal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/legal/info"
          element={
            <ProtectedRoute redirectPath="/login">
              <LegalLSSI />
            </ProtectedRoute>
          }
        />
        <Route
          path="/info-legal"
          element={
            <ProtectedRoute redirectPath="/login">
              <InfoLegal />
            </ProtectedRoute>
          }
        />
        <Route path="/cookies-policy" element={<CookiesPolicy />} />
        <Route path="/privacity" element={<PrivacityPolicy />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
